/**
 * Weld Analytics–owned weld-status client.
 * Uses the Limits/Production API base URL but never writes to the shared weldData store.
 */
import get from 'lodash/get';
import weldDataAxios from '../../../redux/WeldData/axiosConfig';
import {
  mergeWeldDataResults,
  normalizePipeIds,
  tagWeldRowsWithPipe,
} from '../../../redux/WeldData/weldStatusMerge';

const FANOUT_CONCURRENCY = 3;

/** Raw weld-status API row (subset we transform). */
interface WeldStatusApiRow {
  id?: string | number;
  weldNumber?: string;
  wpsNumber?: string;
  projectId?: string | number;
  projectName?: string;
  job_number?: string | string[];
  weld_time?: string | number;
  start_time?: string | number | null;
  end_time?: string | number | null;
  statusByInstantaneous?: boolean | string;
  statusByAvgZone?: boolean | string;
  statusByAvgPass?: boolean | string;
  statusByAvgTilt?: boolean | string;
  gps?: unknown;
  min_distance?: number | null;
  num_of_gps_points?: number | null;
  pipe_diameter?: number | string | null;
  wall_thickness?: number | string | null;
}

export interface TransformedWeldStatusRow {
  id?: string | number;
  weldNumber?: string;
  wpsNumber?: string;
  weldIdentifier: string;
  status: string;
  weldTime: string;
  startTime?: string | number | null;
  endTime?: string | number | null;
  projectId?: string | number;
  projectName?: string;
  job_number?: string | string[];
  statusByInstantaneous?: boolean | string;
  statusByAvgZone?: boolean | string;
  statusByAvgPass?: boolean | string;
  statusByAvgTilt?: boolean | string;
  gps?: unknown;
  minDistance?: number | null;
  numOfGpsPoints?: number | null;
  pipe_diameter?: number | string | null;
  wall_thickness?: number | string | null;
}

export interface FetchWeldStatusForAnalyticsParams {
  projectId: string | number;
  projectName?: string | null;
  pipeIds: Array<string | number>;
  signal?: AbortSignal;
}

export interface FetchWeldStatusForAnalyticsResult {
  weldData: TransformedWeldStatusRow[];
  statusCalculationMethod: string;
  partialErrors: Array<{ pipeId: string | null; error: string }>;
}

type PipeFetchSettled = {
  pipeId: string;
  statusCalculationMethod?: string;
  weldData: TransformedWeldStatusRow[];
  error?: string;
};

function asErrorFields(err: unknown): { name?: string; code?: string; message?: string; error?: string } {
  if (err && typeof err === 'object') {
    return err as { name?: string; code?: string; message?: string; error?: string };
  }
  return { message: err != null ? String(err) : undefined };
}

export function transformWeldStatusRecord(
  weld: WeldStatusApiRow,
  statusCalculationMethod?: string | null,
): TransformedWeldStatusRow {
  let status = 'Completed';
  if (statusCalculationMethod) {
    if (statusCalculationMethod === 'Instantaneous') {
      status = weld.statusByInstantaneous ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByZone') {
      status = weld.statusByAvgZone ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByPass') {
      status = weld.statusByAvgPass ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByTilt') {
      status = weld.statusByAvgTilt ? 'Completed' : 'Rejected';
    }
  } else if (
    weld.statusByInstantaneous === 'NULL' ||
    weld.statusByAvgZone === 'NULL' ||
    weld.statusByAvgPass === 'NULL' ||
    weld.statusByAvgTilt === 'NULL'
  ) {
    status = 'Rejected';
  } else if (
    weld.statusByInstantaneous === 'FALSE' ||
    weld.statusByAvgZone === 'FALSE' ||
    weld.statusByAvgPass === 'FALSE' ||
    weld.statusByAvgTilt === 'FALSE'
  ) {
    status = 'In Progress';
  }

  return {
    id: weld.id,
    weldNumber: weld.weldNumber,
    wpsNumber: weld.wpsNumber,
    weldIdentifier: `WLD-${weld.projectId}-${weld.weldNumber}`,
    status,
    weldTime: `${weld.weld_time} mins`,
    startTime: weld.start_time,
    endTime: weld.end_time,
    projectId: weld.projectId,
    projectName: weld.projectName,
    job_number: weld.job_number,
    statusByInstantaneous: weld.statusByInstantaneous,
    statusByAvgZone: weld.statusByAvgZone,
    statusByAvgPass: weld.statusByAvgPass,
    statusByAvgTilt: weld.statusByAvgTilt,
    gps: weld.gps,
    minDistance: weld.min_distance,
    numOfGpsPoints: weld.num_of_gps_points,
    pipe_diameter: weld.pipe_diameter,
    wall_thickness: weld.wall_thickness,
  };
}

async function fetchWeldStatusForPipe(
  projectId: string | number,
  projectName: string | null | undefined,
  pipeId: string,
  signal?: AbortSignal,
): Promise<{ weldData: TransformedWeldStatusRow[]; statusCalculationMethod: string }> {
  let url = `/weld-status?project_id=${projectId}`;
  if (projectName) {
    url += `&project_name=${encodeURIComponent(projectName)}`;
  }
  // Never send job_number with pipe_details_id — API returns 500.
  if (pipeId) {
    url += `&pipe_details_id=${encodeURIComponent(pipeId)}`;
  }

  const res = await weldDataAxios.get(url, { signal });

  if (res.status >= 200 && res.status < 300) {
    const statusCalculationMethod = get(res, ['data', 'data', 'statusCalculationMethod']) as
      | string
      | undefined;
    const weldData = get(res, ['data', 'data', 'data'], []) as WeldStatusApiRow[];
    const transformed = (weldData || []).map((weld: WeldStatusApiRow) =>
      transformWeldStatusRecord(weld, statusCalculationMethod),
    );
    return {
      weldData: transformed,
      statusCalculationMethod: statusCalculationMethod || 'Instantaneous',
    };
  }
  return { weldData: [], statusCalculationMethod: 'Instantaneous' };
}

/**
 * Fan-out weld-status by pipe for Weld Analytics only.
 */
export async function fetchWeldStatusForAnalytics({
  projectId,
  projectName,
  pipeIds,
  signal,
}: FetchWeldStatusForAnalyticsParams): Promise<FetchWeldStatusForAnalyticsResult> {
  const pipes = normalizePipeIds(pipeIds) as string[];
  if (!projectId) {
    throw new Error('projectId is required');
  }
  if (pipes.length === 0) {
    throw new Error('At least one pipe is required');
  }

  const settled: Array<PipeFetchSettled | undefined> = new Array(pipes.length);
  let next = 0;
  const workers = Array.from(
    { length: Math.min(FANOUT_CONCURRENCY, pipes.length) },
    async () => {
      while (next < pipes.length) {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        const i = next;
        next += 1;
        const pid = pipes[i];
        try {
          const result = await fetchWeldStatusForPipe(projectId, projectName, pid, signal);
          settled[i] = {
            pipeId: pid,
            statusCalculationMethod: result.statusCalculationMethod,
            weldData: tagWeldRowsWithPipe(result.weldData, pid),
          };
        } catch (err: unknown) {
          const e = asErrorFields(err);
          if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
            throw err;
          }
          settled[i] = {
            pipeId: pid,
            error: e.message || e.error || 'Failed to load weld-status for pipe',
            weldData: [],
          };
        }
      }
    },
  );
  await Promise.all(workers);

  const merged = mergeWeldDataResults(settled) as FetchWeldStatusForAnalyticsResult;
  if (merged.weldData.length === 0 && merged.partialErrors.length === pipes.length) {
    throw new Error(
      merged.partialErrors.map((e) => `pipe ${e.pipeId}: ${e.error}`).join('; ')
        || 'Failed to load weld data for all selected pipes',
    );
  }
  return merged;
}
